import React, { useMemo, useState, useEffect } from 'react'
import { Button, Card, Table, Popover, Descriptions, message } from 'antd'
import { IndicatorFilter, LeadIntentionAndPipelineItem, LeadIntentionAndPipelineByTagListFilter, IFollowFormItem, FormItemType, Indicator } from '@types'
import { CommonFilters, TableDownload, UserAvatar } from '@components'
import { useTableService } from '@hooks/useTableService'
import { filterMap, sdrStaticRecallIndicator } from '@pages/task/Workspace/config'
import { getSortOrder, getTableXwidth, TableIdEnum } from '@configs/table'
import { useTableColumn } from '@hooks'
import { Link } from 'react-router-dom'
import { antdSortMap } from '@configs/map'
import { TableProps } from 'antd/lib/table'
import { trimStr, formatTime, notEmptyArray, isNullOrUndefined } from '@library'
import { clone } from 'lodash'
import { FieldItem } from 'src/components/CommonFilters/configs'
import { exportLeadIntentionAndPipelineList, getSDRLeadListByTag } from '@services/lead'
import { ConfigTableHeader } from 'src/components/ConfigTableHeader'
import { getFollowFormItemList } from '@services/customer'
import { storeCustomerQueue } from '@pages/customer/detail/utils'

export interface IndicatorCustomerProp {
  filterKey: keyof typeof filterMap
  initFilter: Partial<IndicatorFilter & LeadIntentionAndPipelineByTagListFilter>
  pane: Indicator
}

const IndicatorLead = (prop: IndicatorCustomerProp) => {
  const { initFilter, pane } = prop
  // 表格服务
  const {
    loading,
    list,
    pagination,
    filter,
    setFilter,
    resetFilter,
    rowKey,
    rowSelection,
    indexList,
  } = useTableService(getSDRLeadListByTag, initFilter, '获取指标线索列表失败')
  // 是否是市场部的复呼任务模块  市场部复呼任务需要增加表格行的选中功能以及开始跟进功能
  const isSdrStaticRecallIndicator = useMemo(() => {
    return sdrStaticRecallIndicator.some(item => item.tag === pane?.tag)
  }, [pane?.tag])
  const [followFormItemList, setFollowFormItemList] = useState<IFollowFormItem[]>([])
  useEffect(() => {
    const queryFollowFormItemList = async () => {
      const [res, err] = await getFollowFormItemList()
      if (err) {
        message.error(`获取渲染表单数据失败，请刷新页面重试(${err.message})`)
      } else {
        setFollowFormItemList(res?.list ?? [])
      }
    }
    queryFollowFormItemList()
  }, [])
  const intentionLeadList = useMemo<any[]>(() => list?.map(item => {
    const intentionLeadItem: any = clone(item)
    const followRecordExtend = intentionLeadItem?.followRecord?.extend ? JSON.parse(intentionLeadItem?.followRecord?.extend) : []
    followRecordExtend.forEach(item => {
      const formItem = followFormItemList.find(formItem => formItem.key === item.key)
      if (formItem) {
        if (formItem.type === FormItemType.SELECT || formItem.type === FormItemType.MUTIL_SELECT) {
          item.keyValue = formItem.options.find(option => option.optionValue === item.keyValue)?.optionName
        }
      }
    })
    intentionLeadItem.interestProduct = intentionLeadItem.interestSmallProductName && `${intentionLeadItem.interestSmallProductName}-${intentionLeadItem.interestSubProductName}`
    intentionLeadItem.customerIntention = `${intentionLeadItem.intentionName}-${intentionLeadItem.subIntentionName}`
    intentionLeadItem.details = [
      { keyName: '本次联系人', keyValue: intentionLeadItem?.followRecord?.touchCustomerName },
      { keyName: '本次沟通方式', keyValue: intentionLeadItem?.followRecord?.followType },
      { keyName: '沟通是否有效', keyValue: intentionLeadItem?.followRecord?.communicateResult },
      { keyName: '客户最新意向', keyValue: `${intentionLeadItem?.followRecord?.intentionName}-${intentionLeadItem?.followRecord?.subIntentionName}` },
      { keyName: '本次跟进内容', keyValue: intentionLeadItem?.followRecord?.remark },
      { keyName: '感兴趣的产品', keyValue: intentionLeadItem?.followRecord?.interestSmallProductName && `${intentionLeadItem?.followRecord?.interestSmallProductName}-${intentionLeadItem?.followRecord?.interestSubProductName}` },
      ...followRecordExtend,
    ]
    return intentionLeadItem
  }), [list])
  // 筛选项配置
  const intentionFilterItems: FieldItem[] = [
    { fieldName: 'customerName', label: '客户名称', type: 'customerNameSelect' },
    { fieldName: 'taskId', label: '任务ID', type: 'input' },
    { fieldName: 'taskName', label: '任务名称', type: 'input' },
    { fieldName: 'batchId', label: '批次id', type: 'batchId' },
    {
      fieldName: 'taskCreateTime',
      label: '任务创建时间',
      type: 'rangePicker',
      handleValue: val => ({
        taskCreateTimeFrom: formatTime(val?.[0], 'start'),
        taskCreateTimeTo: formatTime(val?.[1], 'end'),
      }),
    },
    {
      fieldName: 'createTime',
      label: '线索创建时间',
      type: 'rangePicker',
      handleValue: val => ({
        createTimeFrom: formatTime(val?.[0], 'start'),
        createTimeTo: formatTime(val?.[1], 'end'),
      }),
    },
  ]

  // 表格列定义
  const columns = [
    {
      title: '线索ID',
      dataIndex: 'leadId',
      width: 120,
    },
    {
      title: '线索名称',
      dataIndex: 'leadName',
      width: 150,
    },
    {
      title: '客户名称',
      dataIndex: 'name',
      width: 150,
      render: (name, record) => (
        <Link target="_blank" to={`/customer/${record.customerId}?from=${encodeURIComponent(window.location.pathname)}`}>
          {trimStr(name, '-')}
        </Link>
      ),
    },
    {
      title: '客户意向',
      dataIndex: 'customerIntention',
      width: 120,
    },
    {
      title: '感兴趣的产品',
      dataIndex: 'interestProduct',
      width: 150,
    },
    {
      title: '本次跟进内容',
      dataIndex: 'followContent',
      width: 200,
      ellipsis: true,
    },
    {
      title: '联系人名称',
      dataIndex: 'contactName',
      width: 120,
    },
    {
      title: '职务',
      dataIndex: 'contactPosition',
      width: 120,
    },
    {
      title: '跟进人',
      dataIndex: 'follower',
      width: 220,
      render: (text) => <UserAvatar salesName={text} />,
    },
    {
      title: '跟进时间',
      dataIndex: 'followTime',
      width: 150,
      // sorter: true,
      // sortOrder: filter.sort === 'followTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '线索创建时间',
      dataIndex: 'createTime',
      width: 150,
      sorter: true,
      sortOrder: filter.sort === 'createTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '任务ID',
      dataIndex: 'taskId',
      width: 120,
    },
    {
      title: '任务名称',
      dataIndex: 'taskName',
      width: 150,
    },
    {
      title: '任务创建时间',
      dataIndex: 'taskCreateTime',
      width: 150,
      sorter: true,
      sortOrder: filter.sort === 'taskCreateTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '批次ID',
      dataIndex: 'batchId',
      width: 120,
    },
    {
      title: '批次名称',
      dataIndex: 'batchId',
      width: 150,
    },
    {
      title: '详情',
      dataIndex: 'details',
      width: 80,
      render: (text) => (
        <Popover content={
          <Descriptions column={2} size="small" style={{ width: '600px' }}>
            {text?.map?.(item => {
              return <Descriptions.Item key={item.keyName} label={item.keyName}>{item.keyValue === 'undefined' ? '-' : item.keyValue}</Descriptions.Item>
            })}
          </Descriptions>
        } title="详情">
          <Button type="link" size="small">详情</Button>
        </Popover>
      ),
    },
  ]

  const [columnConfigs, columnProps, forceRequest] = useTableColumn(TableIdEnum.LEAD_INTENTION, columns, [])
  const tableXWidth = useMemo(() => getTableXwidth(columnProps), [columnProps])

  const onTableChange: TableProps<LeadIntentionAndPipelineItem>['onChange'] = (pagination, filters, sorter) => {
    const sort = getSortOrder<LeadIntentionAndPipelineByTagListFilter>(sorter)
    setFilter(sort)
  }

    // 开始跟进
    const handleFollow = () => {
      const isSelectOnly = notEmptyArray(indexList)
      if (!isSelectOnly) {
        message.info('请选中线索后再开始跟进')
        return
      }
      const customerIds = isSelectOnly ? indexList.map(index => list[index].customerId) : list?.map(item => item?.customerId)

      const customerId = customerIds?.[0]
      if (isNullOrUndefined(customerId)) {
        message.warn('当前无可跟进客户')
        return
      }

      const queue = storeCustomerQueue({
        customerIds,
        filter: isSelectOnly ? undefined : filter,
        serviceName: isSelectOnly ? undefined : 'getSDRLeadListByTag',
      })
      window.open(`/customer/${customerId}?from=${encodeURIComponent(window.location.pathname)}&queue=${queue}`, '_blank')
    }
  return (
    <>
      <Card>
        <CommonFilters
          className="custom-Label"
          itemList={intentionFilterItems}
          onSubmit={(filter) => {
            setFilter({ ...filter, page: 1 })
          }}
          onReset={() => resetFilter(initFilter)}
        />
      </Card>
      <Card>
        <ConfigTableHeader
          columnConfigs={columnConfigs}
          tableId={TableIdEnum.LEAD_INTENTION}
          onSuccess={forceRequest}
        />
        <TableDownload
          className="ml20"
          exportMethod={exportLeadIntentionAndPipelineList}
          filter={filter}
        />
        {isSdrStaticRecallIndicator && <Button className="ml10" onClick={() => handleFollow()}>开始跟进</Button>}
        <Table
          style={{ maxWidth: tableXWidth + 'px' }}
          tableLayout="fixed"
          className="mt20"
          loading={loading}
          rowSelection={isSdrStaticRecallIndicator ? rowSelection : null}
          pagination={pagination}
          scroll={{ x: tableXWidth }}
          rowKey={rowKey}
          dataSource={intentionLeadList}
          columns={columnProps}
          onChange={onTableChange}
        />
      </Card>
    </>
  )
}

export default IndicatorLead
